import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { CheckCircle, Circle, MagnifyingGlass, Users, Clock, ArrowLeft, DownloadSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function EventRoster() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState({});
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = async () => {
    try { const { data } = await api.get(`/events/${id}/roster`); setData(data); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Roster unavailable"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 15000);  // live-ish refresh every 15s
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [autoRefresh, id]);

  const checkin = async (rid) => {
    setBusy((b) => ({ ...b, [rid]: true }));
    try {
      const { data: r } = await api.post(`/events/checkin/${rid}`);
      toast.success(r.already ? "Already checked in" : "Checked in ✓");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy((b) => ({ ...b, [rid]: false })); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get(`/events/${id}/roster.csv`, { responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const fname = match ? match[1] : `${(data?.event_title || "roster").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-roster.csv`;
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't export"); }
  };

  const rsvps = data?.rsvps || [];
  const filtered = useMemo(() => {
    if (!q) return rsvps;
    const k = q.toLowerCase();
    return rsvps.filter((r) => (r.user_name || "").toLowerCase().includes(k) || (r.user_email || "").toLowerCase().includes(k) || (r.ticket_number || "").toLowerCase().includes(k));
  }, [q, rsvps]);

  if (err) return <div className="max-w-4xl mx-auto px-6 py-16 samaaj-card p-8 text-center text-[#5C564D]">{err}</div>;
  if (!data) return <div className="max-w-4xl mx-auto px-6 py-16 text-center text-[#5C564D]">Loading roster…</div>;

  const fillPercent = data.capacity ? Math.round((data.total_rsvps / data.capacity) * 100) : null;
  const checkinPercent = data.total_rsvps ? Math.round((data.total_checked_in / data.total_rsvps) * 100) : 0;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10">
      <Link to={`/e/${id}`} className="flex items-center gap-1 text-sm text-[#2C4A3B] hover:underline"><ArrowLeft size={14} /> Back to event</Link>

      <div className="mt-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-[#D95A41]">Attendee roster</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight" data-testid="roster-event-title">{data.event_title}</h1>
          <div className="text-xs text-[#5C564D] mt-1 flex items-center gap-1"><Clock size={12} /> {new Date(data.event_date).toLocaleString()}</div>
        </div>
        <label className="text-xs text-[#5C564D] flex items-center gap-2">
          <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="toggle-auto-refresh" />
          Live refresh
        </label>
        <button onClick={exportCsv} data-testid="btn-export-csv" className="samaaj-btn-outline text-xs flex items-center gap-1">
          <DownloadSimple size={14} /> Export CSV
        </button>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <StatCard label="Total RSVPs" value={data.total_rsvps} sub={data.capacity ? `of ${data.capacity}${fillPercent !== null ? ` · ${fillPercent}% full` : ""}` : "no cap"} testid="stat-rsvps" />
        <StatCard label="Checked in" value={data.total_checked_in} sub={`${checkinPercent}% of RSVPs`} accent testid="stat-checkedin" />
        <StatCard label="Yet to arrive" value={Math.max(0, data.total_rsvps - data.total_checked_in)} sub="live" testid="stat-pending" />
      </div>

      <div className="mt-6">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5C564D]" />
          <input data-testid="roster-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email, or ticket number" className="w-full pl-9 pr-3 py-2 rounded-full border border-[#E5D9CC] bg-[#F9F6F0] text-sm" />
        </div>
      </div>

      <div className="mt-4 samaaj-card overflow-hidden" data-testid="roster-table">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-[#5C564D] text-sm">{q ? "No matches." : "No RSVPs yet."}</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F2EBE5] border-b border-[#E5D9CC]">
              <tr>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#5C564D]">Attendee</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#5C564D] hidden sm:table-cell">Email</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#5C564D]">Ticket</th>
                <th className="text-left px-4 py-3 text-xs uppercase tracking-wider text-[#5C564D]">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-[#E5D9CC] last:border-b-0" data-testid={`roster-row-${r.id}`}>
                  <td className="px-4 py-3"><div className="font-semibold text-[#1C1B1A]">{r.user_name}</div></td>
                  <td className="px-4 py-3 text-[#5C564D] hidden sm:table-cell">{r.user_email}</td>
                  <td className="px-4 py-3 font-mono text-xs">{r.ticket_number}</td>
                  <td className="px-4 py-3">
                    {r.checked_in
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold"><CheckCircle size={14} weight="fill" /> Checked in</span>
                      : <span className="inline-flex items-center gap-1 text-[#5C564D] text-xs"><Circle size={14} /> Pending</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!r.checked_in && (
                      <button data-testid={`btn-checkin-${r.id}`} disabled={busy[r.id]} onClick={() => checkin(r.id)} className="samaaj-btn-primary text-xs">
                        {busy[r.id] ? "…" : "Check in"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, accent, testid }) {
  return (
    <div className={`samaaj-card p-4 ${accent ? "bg-[#2C4A3B] text-[#F9F6F0] border-[#2C4A3B]" : ""}`} data-testid={testid}>
      <div className={`text-[10px] uppercase tracking-widest ${accent ? "text-[#F9F6F0]/70" : "text-[#5C564D]"}`}>{label}</div>
      <div className="font-heading text-3xl mt-1 flex items-center gap-2">{value}{accent && <Users size={16} weight="duotone" />}</div>
      <div className={`text-xs mt-1 ${accent ? "text-[#F9F6F0]/70" : "text-[#5C564D]"}`}>{sub}</div>
    </div>
  );
}
